import { Component, OnInit } from '@angular/core';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { IntegrationService } from 'src/app/services/integration.service';
import { PBXService } from 'src/app/services/pbx.service';

import { SubscriptionResponse, SubscriptionService } from '../shell/subscription.service';
import { Pagination } from './components/pagination/pagination.component';
import {
  CallLogEvent,
  CallLogFilters,
  CallLogResponse,
  DEFAULT_CALL_HISTORY_PAGE_SIZE,
  UICallLogEvent,
} from './models/call-history.model';
import { CallHistoryService } from './services/call-history.service';

const DEFAULT_FILTERS: CallLogFilters = {
  date_time_ranges: [
    {
      from: moment().subtract(1, 'months').second(0).toDate(),
      to: moment().second(0).toDate(),
      time: {
        from: moment(new Date()).seconds(0).minutes(0).hours(0).toDate(),
        to: moment(new Date()).seconds(0).minutes(59).hours(23).toDate(),
      },
    },
  ],
  timezone: 'Europe/Rome',
};
const POLLING_TIME = 3000;

@Component({
  selector: 'app-call-history',
  templateUrl: './call-history.component.html',
  styleUrls: ['./call-history.component.scss'],
})
export class CallHistoryComponent implements OnInit {
  public pageSize: number;
  public pagination: Pagination = {
    page: 0,
    empty: true,
    totalPages: 1,
  };

  public filters: CallLogFilters = DEFAULT_FILTERS;
  public loading = false;
  public error = false;
  public callLog: UICallLogEvent[] = [];
  public sortString: string;
  public isPolling = false;
  public isLiveEnabled = false;
  public showDetails = false;
  public selectedRecord: UICallLogEvent;
  public subscription: SubscriptionResponse;
  public showFilter = false;
  public hideFilterOnInitPage = true;
  public appliedFiltersNumber = 0;

  private pollingInterval: number | null = null;
  private aiProcessingIds: number[] = [];
  private accountId: string;

  constructor(
    private callHistoryService: CallHistoryService,
    private subscriptionService: SubscriptionService,
    private pbxService: PBXService,
    private integrationService: IntegrationService
  ) {}

  ngOnInit(): void {
    this.integrationService.setHasAiAgent();
    this.loading = true;
    this.subscriptionService.getSubscription().subscribe({
      next: (res: SubscriptionResponse) => {
        this.subscription = res;
        this.getCallLog();
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  public changeCurrentPage(event: number): void {
    this.pagination = {
      page: event,
      empty: this.callLog.length === 0,
      totalPages: this.pagination.totalPages,
    };
    this.getCallLog();
  }

  public selectPageSize(event: number): void {
    this.pageSize = event;
    this.pagination.page = 0;
    this.getCallLog();
  }

  public get aiAssistant(): string {
    const addon = this.subscription?.addons.find((a) => a.id.includes('ai_assistant'));
    if (addon) {
      return addon.id.includes('free') ? 'free' : 'paid';
    } else {
      return 'none';
    }
  }

  public openDetails(record: UICallLogEvent): void {
    this.selectedRecord = record;
    this.showDetails = true;
  }

  public closeDeatils(): void {
    this.showDetails = false;
  }

  public openFilterPopup(): void {
    this.showFilter = true;
    this.hideFilterOnInitPage = false;
  }

  public onFiltersChanged(filters: CallLogFilters): void {
    this.filters = filters;
    this.appliedFiltersNumber = Object.keys(this.filters).length;
    this.pagination.page = 0;
    this.getCallLog(true);
  }

  public refreshTable(): void {
    const pbx = this.pbxService.getCachedPbx();
    const timezone = pbx ? pbx.time_zone : DEFAULT_FILTERS.timezone;

    this.filters.date_time_ranges?.forEach((dr) => {
      dr.to = dr.time ? dayjs().format() : `${dayjs().format()}[${timezone}]`;
    });

    this.getCallLog(true);
  }

  private mapCallLogResponse(response: CallLogResponse): void {
    this.callLog = response.content.map((e: CallLogEvent) => this.callHistoryService.parseCallLogEvent(e));
    this.updateProcessingIds();
    this.pagination = {
      page: response.number,
      empty: response.empty,
      totalPages: response.totalPages,
    };
    this.pageSize = response.size;
    this.loading = false;
  }

  private updateProcessingIds(): void {
    this.aiProcessingIds = this.callLog.filter((c) => c.ai.isProcessing).map((c) => c.id);
  }

  private manageErrorResponse(): void {
    this.pagination = {
      page: 0,
      empty: true,
      totalPages: 0,
      hidePageSize: true,
    };
    this.callLog = [];
    this.loading = false;
    this.error = true;
  }

  private getCallLog(isFilterApplaied?: boolean): void {
    this.loading = true;
    this.appliedFiltersNumber = Object.keys(this.filters).length;
    if (!this.filters['timezone']) {
      this.appliedFiltersNumber += 1;
    }
    const accountId$ = isFilterApplaied
      ? of(this.accountId) // If filter is applied, use the existing accountId
      : this.callHistoryService.getAccountId().pipe(
          tap((accountId: string) => {
            this.accountId = accountId;
          })
        );

    accountId$
      .pipe(
        switchMap((accountId: string) =>
          this.callHistoryService.getCallLog(
            this.filters,
            { ...this.pagination, pageSize: this.pageSize || DEFAULT_CALL_HISTORY_PAGE_SIZE },
            accountId,
            this.sortString
          )
        )
      )
      .subscribe({
        next: (res) => {
          this.mapCallLogResponse(res);
          if (this.aiProcessingIds.length > 0) {
            this.startPolling(true);
          }
          if (this.aiProcessingIds.length === 0 && !this.isLiveEnabled) {
            this.stopPolling();
          }
        },
        error: () => {
          this.manageErrorResponse();
        },
      });
  }

  private startPolling(silent = false): void {
    if (this.isPolling || this.pollingInterval !== null) return;
    if (!silent) {
      this.isPolling = true;
    }
    this.pollingInterval = window.setInterval(() => {
      this.callHistoryService
        .getCallLog(
          this.filters,
          { ...this.pagination, pageSize: this.pageSize || DEFAULT_CALL_HISTORY_PAGE_SIZE },
          this.accountId,
          this.sortString
        )
        .subscribe({
          next: (response: CallLogResponse) => {
            this.mapCallLogResponse(response);
            if (this.filters.date_time) {
              this.filters = {
                ...this.filters,
                date_time: {
                  ...this.filters.date_time,
                  to: new Date(),
                },
              };
            }
            if (this.filters.date_time_ranges) {
              this.filters = {
                ...this.filters,
                date_time_ranges: this.filters.date_time_ranges.map((f) => ({
                  ...f,
                  to: new Date(),
                })),
              };
            }
            if (this.aiProcessingIds.length === 0 && !this.isLiveEnabled) {
              this.stopPolling();
            }
          },
          error: () => {
            this.manageErrorResponse();
          },
        });
    }, POLLING_TIME);
  }

  private stopPolling(): void {
    clearInterval(this.pollingInterval!);
    this.pollingInterval = null;
    this.isPolling = false;
  }
}
